import React from 'react';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ProgressButton } from '@/components/ProgressButton';
import { ProductDetails } from '@/components/ProductDetails';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import { JointPolicyDeleteDialog } from '@/components/JointPolicyDeleteDialog';
import { QuoteROIDisplay } from '@/components/quote-cards/QuoteROIDisplay';
import { QuotePriceDisplay } from '@/components/quote-cards/QuotePriceDisplay';
import { QuoteProductInfo } from '@/components/quote-cards/QuoteProductInfo';
import { UnderwritingStatusBadge } from '@/components/UnderwritingStatusBadge';

import { ChevronUp } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useIllustrationPolling } from '@client/hooks/useIllustrationPolling';
import { usePolicyManagement } from '@client/hooks/usePolicyManagement';
import { useIllustrationManagement } from '@client/hooks/useIllustrationManagement';
import { useQuoteCardDialogs } from '@client/hooks/useQuoteCardDialogs';
import { getPolicyTypeFromProductName } from '@client/util/findPartnerPolicyFundingSourceId';

import { Quote, selectClient } from '@client/model';

import { cn } from '@/lib';

interface QuoteWithROI extends Quote {
  roi?: number;
  formattedROI?: string;
  roiValues?: any;
}

export type ShopInsuranceQuoteCardProps = {
  quote: Quote | QuoteWithROI;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  hasHighestROI?: boolean;
  isSelected?: boolean;
  fundingSourceId?: string | null;
  selectedTab?: ShopInsuranceTab;
  clientPartner?: Client | null;
  selectedPremium?: number;
  underwritingResult?: 'eligible' | 'ineligible' | 'unsure' | null;
  onViewUnderwritingDetails?: () => void;
  testId?: string
};

export function LifeInsuranceQuoteCard({
  quote,
  isExpanded,
  onToggleExpand,
  onClose,
  hasHighestROI = false,
  isSelected = false,
  fundingSourceId = null,
  selectedTab = 'single',
  clientPartner = null,
  selectedPremium,
  underwritingResult,
  onViewUnderwritingDetails,
  testId
}: ShopInsuranceQuoteCardProps) {
  const client = useSelector(selectClient);
  useIllustrationPolling();

  const {
    handleAddPolicy,
    handleRemovePolicy,
    deletePolicyForClientOnly,
    deletePolicyForBothClients,
  } = usePolicyManagement({
    quote,
    selectedTab,
    clientPartner,
    fundingSourceId,
    onClose,
  });

  const { hasIllustrationPdf, isCreatingIllustration, handleViewPdf, handleCreateIllustration } =
    useIllustrationManagement({
      quote,
      selectedPremium,
      clientPartner,
    });

  const {
    showJointPolicyDeleteDialog,
    jointPolicyDeleteMode,
    showQuoteGenerationInfoDialog,
    setJointPolicyDeleteMode,
    handleJointPolicyDeleteCancel,
    handleJointPolicyDeleteConfirm,
    showJointDialog,
    showQuoteGenerationInfoDialogFn,
    hideQuoteGenerationInfoDialog,
  } = useQuoteCardDialogs();

  const quoteWithROI = quote as QuoteWithROI;
  const displayROI = quoteWithROI.formattedROI;

  return (
    <>
      <Card
        data-testid={testId}
        className={cn(
          'border-primary/20 hover:border-primary/40 w-full cursor-pointer rounded-xl p-5 transition-all duration-200 hover:shadow-xl hover:shadow-black/5',
          {
            'border-primary': isSelected,
          },
        )}
        onClick={isSelected ? undefined : onToggleExpand}
      >
        <div className="relative">
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex flex-col gap-2">
              <QuoteProductInfo
                carrierName={quote.carrierName}
                productName={quote.productName}
                showLogo={true}
                showRating={true}
              />
              <UnderwritingStatusBadge
                status={underwritingResult ?? null}
                onViewDetails={
                  underwritingResult && onViewUnderwritingDetails
                    ? onViewUnderwritingDetails
                    : undefined
                }
              />
            </div>
            <QuoteROIDisplay
              projectedCostCoverage={quoteWithROI.projectedCostCoverage}
              policyType={getPolicyTypeFromProductName(
                quote.productName,
                quote.policyType === 'Joint Life',
              )}
              displayROI={displayROI}
            />
            <QuotePriceDisplay
              price={quote.premiumAmount}
              fundingOption={quote.productFundingOption}
            />
            <div className="ml-auto flex flex-col gap-2">
              {isSelected ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="rounded-lg bg-red-500/10 text-red-600 hover:bg-red-500/15"
                  onClick={event => handleRemovePolicy(event, showJointDialog)}
                >
                  Remove Product
                </Button>
              ) : (
                <Button
                  type="button"
                  className="rounded-lg"
                  onClick={handleAddPolicy}
                >
                  Select Product
                </Button>
              )}
              <ProgressButton
                testId="shop-insurance-quote-download-button"
                onClick={
                  hasIllustrationPdf
                    ? handleViewPdf
                    : event => handleCreateIllustration(event, showQuoteGenerationInfoDialogFn)
                }
                disabled={isCreatingIllustration}
                isLoading={isCreatingIllustration}
                isCompleted={Boolean(hasIllustrationPdf)}
                defaultText="Create Quote"
                loadingText="Generating"
                completedText="View Quote"
                className="w-full"
              />
            </div>
          </div>
          <div className="border-primary/20 mt-4 border-t">
            <div
              data-testid="shop-insurance-quote-expand-button"
              className={cn(
                'flex items-center justify-between pt-5',
                !isSelected && 'cursor-pointer',
              )}
              onClick={event => {
                event.stopPropagation();
                onToggleExpand();
              }}
            >
              <span className="sm:text-md text-primary-dark select-none text-sm font-medium">
                Product Details
              </span>
              <ChevronUp
                className={cn('text-primary-dark size-5 transition-transform duration-200', {
                  'rotate-180': isExpanded,
                })}
              />
            </div>
          </div>
          {isExpanded && <ProductDetails quote={quote} />}
        </div>
      </Card>

      <JointPolicyDeleteDialog
        open={showJointPolicyDeleteDialog}
        onClose={handleJointPolicyDeleteCancel}
        onConfirm={mode =>
          handleJointPolicyDeleteConfirm(
            mode,
            deletePolicyForBothClients,
            deletePolicyForClientOnly,
          )
        }
        deleteMode={jointPolicyDeleteMode}
        onDeleteModeChange={setJointPolicyDeleteMode}
        clientName={client?.clientFirstName}
        partnerName={clientPartner?.clientFirstName}
      />

      <UnsavedChangesDialog
        showWarning={showQuoteGenerationInfoDialog}
        handleCancelClose={hideQuoteGenerationInfoDialog}
        handleConfirmClose={hideQuoteGenerationInfoDialog}
        dialogTitle="Please wait for current quote generation"
        dialogDescription="We're currently generating a quote for another product. Since this process takes 1-2 minutes, please wait for it to complete before creating a new quote. You can still view any previously generated quotes."
        primaryButtonText="Got it"
        secondaryButtonText=""
        destructive={false}
        showIcon={true}
      />
    </>
  );
}
